import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import './ActiveWork.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ActiveWork = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const socket = useSocket();

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${API_BASE}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setContracts(data.contracts || []);
      } else {
        setError(data.message || 'Failed to fetch active contracts');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => fetchContracts();
    socket.on('contract_status_change', refresh);
    socket.on('payment_verified', refresh);
    socket.on('work_completed', refresh);
    socket.on('payment_released', refresh);
    return () => {
      socket.off('contract_status_change', refresh);
      socket.off('payment_verified', refresh);
      socket.off('work_completed', refresh);
      socket.off('payment_released', refresh);
    };
  }, [socket, fetchContracts]);

  const filteredContracts = useMemo(() => {
    return contracts.filter(c =>
      c.vesselName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.serviceType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c._id?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, contracts]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_payment':
        return <span className="w-status is-pending">Awaiting Deposit</span>;
      case 'payment_uploaded':
        return <span className="w-status is-review">Verifying Deposit</span>;
      case 'payment_verified':
        return <span className="w-status is-ready">Ready to Start</span>;
      case 'work_in_progress':
        return <span className="w-status is-progress">In Progress</span>;
      case 'work_completed':
        return <span className="w-status is-completed">Pending Release</span>;
      case 'payment_released':
        return <span className="w-status is-paid">Paid & Closed</span>;
      default:
        return <span className="w-status">{status}</span>;
    }
  };

  if (loading) return <div className="w-loader-wrap"><i className="fas fa-circle-notch fa-spin"></i></div>;

  return (
    <div className="w-clean-container">
      <div className="w-clean-header">
        <div className="w-header-info">
          <h1>My Work</h1>
          <p>Manage execution, operational tracking, and financial tracking for active vessel orders</p>
        </div>
        <button className="w-action-primary-btn" onClick={fetchContracts}>
          <i className="fas fa-sync-alt"></i> Sync Board
        </button>
      </div>

      <div className="w-toolbar-strip">
        <div className="w-search-input-wrapper">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Filter operations by vessel, order ID, or service..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-records-badge">
          {filteredContracts.length} active tasks
        </div>
      </div>

      {error ? (
        <div className="w-panel-state error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
        </div>
      ) : filteredContracts.length === 0 ? (
        <div className="w-panel-state empty">
          <i className="fas fa-tools"></i>
          <h3>No projects on the horizon</h3>
          <p>Your operations log is empty. Quotes accepted by managers will materialize here.</p>
        </div>
      ) : (
        <div className="w-table-card">
          <table className="w-table-minimal">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Vessel</th>
                <th>Port Location</th>
                <th>Service Type</th>
                <th>Status</th>
                <th className="text-right">Operations</th>
              </tr>
            </thead>
            <tbody>
              {filteredContracts.map(contract => (
                <tr key={contract._id}>
                  <td className="w-id-text">#{contract._id?.slice(-8).toUpperCase()}</td>
                  <td className="w-vessel-name">{contract.vesselName}</td>
                  <td className="w-port-cell">
                    <i className="fas fa-map-marker-alt"></i> {contract.port || 'TBD'}
                  </td>
                  <td className="w-service-text">{contract.serviceType}</td>
                  <td>{getStatusBadge(contract.status)}</td>
                  <td className="text-right">
                    <div className="w-actions-row">
                      <button 
                        className="w-row-btn" 
                        title="Open Control Panel" 
                        onClick={() => navigate(`/service-provider/contract/${contract._id}`)}
                      >
                        <i className="fas fa-sliders-h"></i> Manage
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActiveWork;